import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Activity, AlertTriangle, RefreshCw, ChevronRight, TrendingUp, Info } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine, Legend } from 'recharts';
import { Button } from '../components/ui/button';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import DashboardLayout from '../components/DashboardLayout';
import PageHeader from '../components/PageHeader';
import MobilitySilhouette, { colorForScore } from '../components/MobilitySilhouette';
import { REGION_DISPLAY, buildRegionTrend, TREND_LINES } from '../lib/mobilityRegions';
import api from '../services/api';
import { toast } from 'sonner';

/**
 * /mobility — Mobility & ROM hub.
 *
 * Layout:
 *  • Compact header: title + retest CTA
 *  • Hero card: overall score (top-left), tinted body silhouette (center),
 *    region scores with mini trajectory sparklines (right)
 *  • Mobility trend chart: overall_mobility over time + per-region overlay
 *  • Asymmetry callouts
 *  • Focus areas (drives weekly plan)
 *
 * Region labels + trend config come from ../lib/mobilityRegions (shared with
 * the /performance Movement page so they never drift).
 */

const RegionRow = ({ regionKey, score, history }) => {
  // Trajectory sparkline (build from history)
  const points = history
    .map(h => ({ date: h.taken_at, score: h?.region_scores?.[regionKey] }))
    .filter(p => p.score != null)
    .sort((a, b) => new Date(a.date) - new Date(b.date));
  const hasTrend = points.length >= 2;
  let trend = null;
  if (hasTrend) {
    const w = 64, h = 18;
    const min = Math.min(...points.map(p => p.score));
    const max = Math.max(...points.map(p => p.score));
    const range = Math.max(1, max - min);
    const step = w / (points.length - 1);
    const pts = points.map((p, i) =>
      `${(i * step).toFixed(1)},${(h - ((p.score - min) / range) * h).toFixed(1)}`
    ).join(' ');
    const last = points[points.length - 1].score;
    const first = points[0].score;
    const delta = last - first;
    trend = (
      <div className="flex items-center gap-1.5">
        <svg width={w} height={h}>
          <polyline points={pts} fill="none" stroke={colorForScore(last)} strokeWidth="1.5" />
        </svg>
        <span className={`text-[10px] ${delta > 0 ? 'text-primary' : delta < 0 ? 'text-red-400' : 'text-zinc-500'}`}>
          {delta > 0 ? `↑+${delta}` : delta < 0 ? `↓${Math.abs(delta)}` : '→'}
        </span>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between text-xs py-1.5 px-2 rounded-sm bg-zinc-950 border border-zinc-800" data-testid={`mobility-region-${regionKey}`}>
      <span className="text-zinc-400 text-[11px] truncate flex-1 min-w-0 pr-2">
        {REGION_DISPLAY[regionKey] || regionKey}
      </span>
      <div className="flex items-center gap-2 flex-shrink-0">
        {trend || <span className="text-zinc-700 text-[10px]">need 2+ tests</span>}
        <span className="font-secondary font-bold text-base w-7 text-right" style={{ color: colorForScore(score) }}>
          {score}
        </span>
      </div>
    </div>
  );
};

// Build chart data from history (shared with the Movement page).
const buildTrendData = buildRegionTrend;

const Mobility = () => {
  const navigate = useNavigate();
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [retesting, setRetesting] = useState(false);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    try {
      const [c, h] = await Promise.all([
        api.getCurrentBaseline(),
        api.getBaselineHistory(),
      ]);
      setCurrent(c.data);
      setHistory(h.data?.baselines || []);
    } catch (e) {
      toast.error('Failed to load mobility data');
    } finally {
      setLoading(false);
    }
  };

  const retake = async () => {
    setRetesting(true);
    try {
      await api.retestBaseline();
      navigate('/assessment');
    } catch (e) {
      toast.error('Could not start retest');
    } finally {
      setRetesting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 flex items-center justify-center min-h-[60vh]">
          <Activity className="w-12 h-12 text-primary animate-pulse" />
        </div>
      </DashboardLayout>
    );
  }

  const baseline = current?.baseline;

  if (!baseline) {
    return (
      <DashboardLayout>
        <div className="p-6 max-w-3xl mx-auto" data-testid="mobility-page-empty">
          <div className="hpme-card p-8 text-center">
            <div className="hpme-card-corner" />
            <Activity className="w-14 h-14 text-primary mx-auto mb-4" />
            <h1 className="font-secondary font-bold text-3xl tracking-tight mb-2">MOBILITY &amp; ROM</h1>
            <p className="text-zinc-400 mb-6 max-w-md mx-auto">
              No baseline yet. Take the 5-minute camera-tracked assessment to map your mobility — your plan adapts to how your body actually moves.
            </p>
            <Button className="hpme-btn-primary" onClick={() => navigate('/assessment')} data-testid="mobility-empty-start">
              Start Assessment <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const regionScores = baseline.region_scores || {};
  const asymmetry = baseline.asymmetry_flags || [];
  const overall = baseline.overall_mobility || 0;
  const trendData = buildTrendData(history);
  const hasMultipleBaselines = trendData.length >= 2;

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="mobility-page">

        {/* Compact header */}
        <PageHeader
          tag="Movement Intelligence"
          tagColor="#CCFF00"
          icon={Activity}
          title="Mobility & ROM"
          subtitle="How your body actually moves — driving every plan."
          testIdPrefix="mobility"
          actions={(
            <>
              {current?.needs_retest && (
                <span className="text-[10px] uppercase font-secondary font-bold px-2 py-1 rounded-sm bg-yellow-500/20 text-yellow-300 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Retest due
                </span>
              )}
              <Button
                variant="outline"
                onClick={retake}
                disabled={retesting}
                data-testid="mobility-retest-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${retesting ? 'animate-spin' : ''}`} />
                {retesting ? 'Starting…' : 'Retest mobility'}
              </Button>
            </>
          )}
        />

        {/* Hero: score + body + region list */}
        <div className="hpme-card p-6 relative">
          <div className="hpme-card-corner" />
          <div className="grid lg:grid-cols-[180px_1fr_320px] gap-6">
            {/* Score column */}
            <div>
              <div className="flex items-center gap-1.5 mb-1">
                <p className="hpme-label">OVERALL</p>
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      type="button"
                      className="text-zinc-500 hover:text-primary transition-colors inline-flex"
                      aria-label="How mobility scores work"
                      data-testid="mobility-info-trigger"
                    >
                      <Info className="w-3.5 h-3.5" />
                    </button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="start"
                    className="w-72 bg-zinc-950 border-zinc-800 text-zinc-300"
                    data-testid="mobility-info-popover"
                  >
                    <p className="text-zinc-200 font-secondary font-bold uppercase text-[11px] tracking-wider mb-2">How mobility scores work</p>
                    <div className="space-y-2 text-[11px] leading-relaxed text-zinc-400">
                      <p>We camera-track joint range across <span className="text-zinc-300">shoulder, hip, ankle, neck, side-bend and low back</span>.</p>
                      <p>Each 0–100 score is your range measured <span className="text-zinc-300">from your own neutral</span> — not a textbook angle — so body shape and camera angle don't bias it.</p>
                      <p>We also check <span className="text-zinc-300">left vs right balance</span> and flag <span className="text-zinc-300">compensation</span> (e.g. trunk lean), so the number reflects real, clean movement.</p>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
              <p className="font-secondary font-bold text-6xl tracking-tight leading-none" style={{ color: colorForScore(overall) }}>
                {overall}
              </p>
              <p className="text-xs text-zinc-500 mt-1">out of 100</p>
              <p className="text-[10px] text-zinc-600 mt-3">
                Mapped {new Date(baseline.taken_at).toLocaleDateString()}
                {current?.next_retest_due && (
                  <><br/>Next retest {new Date(current.next_retest_due).toLocaleDateString()}</>
                )}
              </p>
            </div>

            {/* Body — front + back, full size, with hover tooltips */}
            <div className="flex justify-center items-center min-h-[440px]" data-testid="mobility-silhouette">
              <MobilitySilhouette
                regionScores={regionScores}
                asymmetryFlags={asymmetry}
                size="md"
                showBack
                interactive
              />
            </div>

            {/* Region scores + trajectory */}
            <div className="space-y-1.5" data-testid="mobility-region-list">
              <p className="hpme-label mb-2">REGION SCORES · 90D TRAJECTORY</p>
              {Object.entries(regionScores).map(([k, v]) => (
                <RegionRow key={k} regionKey={k} score={v} history={history} />
              ))}
              <p className="text-[10px] text-zinc-600 mt-2 italic">
                Tap or hover the body to see L vs R values per joint.
              </p>
            </div>
          </div>
        </div>

        {/* Mobility trend chart */}
        <div className="hpme-card p-6">
          <div className="hpme-card-corner" />
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-5 h-5 text-primary" />
            <h2 className="font-secondary font-bold text-xl">MOBILITY TREND</h2>
          </div>
          <p className="text-zinc-400 text-sm mb-4">
            Overall mobility and key joint groups across your baseline retests.
          </p>
          {hasMultipleBaselines ? (
            <div className="h-64 min-h-[256px] min-w-0" data-testid="mobility-trend-chart">
              <ResponsiveContainer width="99%" height="100%">
                <LineChart data={trendData} margin={{ top: 8, right: 12, left: -8, bottom: 0 }}>
                  <XAxis dataKey="date" stroke="#52525B" fontSize={11} />
                  <YAxis stroke="#52525B" fontSize={11} domain={[0, 100]} />
                  <ReferenceLine y={85} stroke="#22C55E" strokeDasharray="3 3" strokeOpacity={0.3} />
                  <ReferenceLine y={55} stroke="#FACC15" strokeDasharray="3 3" strokeOpacity={0.3} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#18181B', border: '1px solid #27272A', borderRadius: '4px', fontSize: '12px' }}
                  />
                  <Legend wrapperStyle={{ fontSize: '11px' }} />
                  {TREND_LINES.map(l => (
                    <Line key={l.key} type="monotone" dataKey={l.key} name={l.name} stroke={l.color}
                      strokeWidth={l.strong ? 2.5 : 1.5} strokeDasharray={l.strong ? undefined : '2 2'}
                      dot={l.strong ? { fill: l.color, r: 3.5 } : { r: 2 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-32 flex flex-col items-center justify-center text-center" data-testid="mobility-trend-empty">
              <Activity className="w-8 h-8 text-zinc-700 mb-2" />
              <p className="text-zinc-500 text-sm">One baseline so far — your trend line starts at retest #2.</p>
              <p className="text-zinc-600 text-xs mt-1">28-day retest cadence keeps the line meaningful.</p>
            </div>
          )}
          <p className="text-[10px] text-zinc-600 mt-2">
            Dashed lines mark Strong (85) and Limited (55) bands. Each dot is one baseline assessment.
          </p>
        </div>

        {/* Asymmetry */}
        {asymmetry.length > 0 && (
          <div className="hpme-card p-6 border-amber-500/30">
            <div className="hpme-card-corner" />
            <p className="hpme-label mb-3 text-amber-300">ASYMMETRY · LEFT vs RIGHT</p>
            <div className="grid md:grid-cols-2 gap-3">
              {asymmetry.map(f => (
                <div key={f.test_id} className="bg-amber-500/5 border border-amber-500/30 rounded-sm p-3" data-testid={`mobility-asym-${f.test_id}`}>
                  <p className="text-sm font-secondary font-bold text-amber-100">{f.test_name}</p>
                  <p className="text-xs text-amber-300 mt-1">
                    Left {f.left_score} · Right {f.right_score} ·
                    <span className="text-amber-400 font-bold ml-1">{f.delta} gap</span>,
                    weaker on the {f.weaker_side === 'L' ? 'left' : 'right'}.
                  </p>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-zinc-500 mt-3">
              Side-to-side gaps over 15 points get flagged. Your plan prioritises the weaker side.
            </p>
          </div>
        )}

        {/* Focus areas */}
        {baseline.weakest_regions?.length > 0 && (
          <div className="hpme-card p-6">
            <div className="hpme-card-corner" />
            <p className="hpme-label mb-3">FOCUS AREAS · driving your plan</p>
            <div className="flex gap-2 flex-wrap">
              {baseline.weakest_regions.map(k => (
                <span key={k} className="px-3 py-1.5 rounded-sm bg-yellow-500/15 text-yellow-300 text-xs uppercase font-semibold">
                  {REGION_DISPLAY[k] || k}
                </span>
              ))}
            </div>
            <p className="text-[11px] text-zinc-500 mt-3 flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Nikki uses these to prepend mobility work to your weekly plan.
            </p>
          </div>
        )}

        <div className="text-center">
          <Link to="/performance" className="text-primary text-sm hover:underline">
            View workout performance trends →
          </Link>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Mobility;
